import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, from } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { SessionExpiredService } from '../services/session-expired.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  /** Single in-flight refresh — peticiones concurrentes con 401 esperan al mismo resultado. */
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly sessionExpired: SessionExpiredService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.keycloakService.getToken()).pipe(
      mergeMap(token => {
        const authed = token ? this.addToken(request, token) : request;

        return next.handle(authed).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              return this.handle401(authed, next, !!token, error);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private handle401(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    hadToken: boolean,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    // Sin token previo y backend dice 401 → no hay nada que refrescar
    if (!hadToken) {
      this.sessionExpired.handle('UNAUTHENTICATED', originalError);
      return throwError(() => originalError);
    }

    if (this.isRefreshing) {
      // Hay un refresh en curso — esperar a que termine y reintentar con el nuevo token
      return this.refreshTokenSubject.pipe(
        filter(t => t !== null),
        take(1),
        mergeMap(newToken => next.handle(this.addToken(request, newToken as string)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return from(this.keycloakService.updateToken(30)).pipe(
      mergeMap(() => from(this.keycloakService.getToken())),
      mergeMap(newToken => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newToken);

        return next.handle(this.addToken(request, newToken)).pipe(
          catchError((retryError: HttpErrorResponse) => {
            // El refresh tuvo éxito pero el backend sigue rechazando → sesión revocada en backend
            if (retryError.status === 401) {
              this.sessionExpired.handle('RETRY_UNAUTHORIZED', retryError);
            }
            return throwError(() => retryError);
          })
        );
      }),
      catchError(refreshError => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);

        // Distinguir refresh fallido por sesión muerta (Keycloak respondió 400/401)
        // vs error de transporte (red caída, Keycloak inalcanzable). El adapter de
        // keycloak-js rechaza con error genérico — tratamos como sesión expirada
        // salvo que claramente sea un error de red sin respuesta del servidor.
        const isNetworkError = this.looksLikeNetworkError(refreshError);
        if (!isNetworkError) {
          this.sessionExpired.handle('REFRESH_FAILED', refreshError);
        }
        return throwError(() => refreshError);
      })
    );
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
  }

  private looksLikeNetworkError(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 0;
    }
    if (error instanceof Error) {
      const msg = error.message?.toLowerCase() || '';
      return msg.includes('network') || msg.includes('failed to fetch');
    }
    return false;
  }
}
